import { useEffect, useState } from 'react';

const isProduction = process.env.NODE_ENV === 'production';

/** How often to poll for a new service-worker.js while the app stays open. */
const SW_UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

/**
 * Minimum gap between proactive checks triggered by focus/visibility events.
 * Returning to a tab often fires both `visibilitychange` and `focus` at once.
 */
const SW_UPDATE_CHECK_DEBOUNCE_MS = 1000;

/** Asks the browser to fetch service-worker.js and install a newer SW if one exists. */
function checkForServiceWorkerUpdate(): void {
  navigator.serviceWorker.ready
    .then((registration) => registration.update())
    .catch(() => {
      // Browser may throttle checks or the client may be offline.
    });
}

/**
 * Detects when a newer RHLS build is available via the registered service worker.
 *
 * Production only — the SW is registered from index.pug in prod builds. Pair with
 * `ServiceWorkerUpdateAlert`, which lets the user refresh on their own schedule.
 *
 * Webpack is configured with `skipWaiting: false`, so the old SW keeps serving a
 * consistent app shell + lazy chunks until the user reloads. This hook only signals
 * that an update is ready; it never forces a reload.
 *
 * Returns `true` when a waiting worker exists (new build installed, old SW still
 * controlling the page). The UI should prompt the user to refresh.
 */
export default function useServiceWorkerUpdate(): boolean {
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);

  useEffect(() => {
    if (!isProduction || !('serviceWorker' in navigator)) {
      return undefined;
    }

    let mounted = true;
    let lastUpdateCheckAt = 0;

    const markUpdateAvailable = () => {
      if (mounted) {
        setIsUpdateAvailable(true);
      }
    };

    // Shared gate for focus-driven checks: skip hidden tabs and debounce duplicates.
    const requestServiceWorkerUpdateCheck = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      const now = Date.now();
      if (now - lastUpdateCheckAt < SW_UPDATE_CHECK_DEBOUNCE_MS) {
        return;
      }

      lastUpdateCheckAt = now;
      checkForServiceWorkerUpdate();
    };

    // User switched back to this browser tab.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        requestServiceWorkerUpdateCheck();
      }
    };

    // User returned to the browser window from another application.
    const handleWindowFocus = () => {
      requestServiceWorkerUpdateCheck();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    const updateIntervalId = window.setInterval(
      checkForServiceWorkerUpdate,
      SW_UPDATE_CHECK_INTERVAL_MS,
    );

    navigator.serviceWorker.ready.then((registration) => {
      // Page loaded while a previous session already downloaded a waiting worker.
      if (registration.waiting && navigator.serviceWorker.controller) {
        markUpdateAvailable();
      }

      // A new SW was found after registration.update() or the browser's own check.
      registration.addEventListener('updatefound', () => {
        const installingWorker = registration.installing;

        if (!installingWorker) {
          return;
        }

        installingWorker.addEventListener('statechange', () => {
          // `controller` being set means this is an update, not the first install.
          if (
            installingWorker.state === 'installed' &&
            navigator.serviceWorker.controller
          ) {
            markUpdateAvailable();
          }
        });
      });
    });

    return () => {
      mounted = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      window.clearInterval(updateIntervalId);
    };
  }, []);

  return isUpdateAvailable;
}
